import { format } from "date-fns";
import { v4 as uuidv4 } from "uuid";

import type {
  JourneyNodeDetails,
  JourneyNoteDetails,
} from "src/pages/journeys/types";
import {
  type JourneyNodeConfig,
  JourneyNodeType,
  type CouponAssignmentConfig,
  type DecisionConfig,
  type EntryCohortConfig,
  type EntryEventConfig,
  type SyncConfig,
  type FunctionConfig,
  type EventEmissionConfig,
  type TimeDelayConfig,
  type WaitUntilEventConfig,
  type SegmentsConfig,
  type SplitsConfig,
  type ContextConfig,
  type MessageNodeConfig,
} from "src/types/journeys";
import { ChannelType } from "src/types/cep";
import { DEFAULT_IN_APP_MESSAGE_EXPIRATION } from "src/cep/campaigns/campaign-detail/schedule/in-app-schedule";
import { ConditionType, IntervalUnit } from "src/types/visual";

type ConfigFromType<
  T extends Exclude<JourneyNodeType, typeof JourneyNodeType.Note>,
> = Extract<JourneyNodeConfig, { type: T }>;

export function getInitialNodeConfiguration<T extends JourneyNodeType>(
  type: T,
  messageType?: MessageNodeConfig["message_type"],
): T extends typeof JourneyNodeType.Note
  ? Omit<JourneyNoteDetails, "position">
  : JourneyNodeDetails<ConfigFromType<Exclude<T, typeof JourneyNodeType.Note>>>;
export function getInitialNodeConfiguration(
  type: JourneyNodeType,
  messageType?: MessageNodeConfig["message_type"],
):
  | JourneyNodeDetails<JourneyNodeConfig>
  | Omit<JourneyNoteDetails, "position"> {
  const id = uuidv4();

  switch (type) {
    case JourneyNodeType.EntryCohort:
      return {
        ...getDefaultNodeDetails(),
        id,
        name: "Start",
        config: {
          type: JourneyNodeType.EntryCohort,
          max_num_entries: 1,
          // @ts-expect-error - event_conditions is not a valid property for EntryCohortConfig. This initializes the form for both EntryCohort and EntryEvent
          event_conditions: {
            type: ConditionType.And,
            conditions: [],
          },
          max_simultaneous_entries: 0,
        },
      } satisfies JourneyNodeDetails<EntryCohortConfig>;
    case JourneyNodeType.EntryEvent:
      return {
        ...getDefaultNodeDetails(),
        id,
        name: "Start",
        config: {
          type: JourneyNodeType.EntryEvent,
          max_num_entries: 1,
          event_conditions: {
            type: ConditionType.And,
            conditions: [],
          },
          lookback_window: {
            relative: {
              unit: IntervalUnit.Day,
              // Initial value is `undefined`, but type requires a number
              quantity: undefined as any,
            },
          },
          max_simultaneous_entries: 0,
        },
      } satisfies JourneyNodeDetails<EntryEventConfig>;
    case JourneyNodeType.Sync:
      return {
        ...getDefaultNodeDetails(),
        id,
        name: "Send to destination",
        config: { type: JourneyNodeType.Sync },
      } satisfies JourneyNodeDetails<SyncConfig>;
    case JourneyNodeType.Function:
      return {
        ...getDefaultNodeDetails(),
        id,
        name: "Call function",
        realtime_function_config: null,
        config: { type: JourneyNodeType.Function },
      } satisfies JourneyNodeDetails<FunctionConfig>;
    case JourneyNodeType.EventEmission:
      return {
        ...getDefaultNodeDetails(),
        id,
        name: "Emit event",
        config: {
          type: JourneyNodeType.EventEmission,
          event_model_id: null,
          mappings: [],
          wait_for_event: true,
          send_to_event_syncs: true,
        },
      } satisfies JourneyNodeDetails<EventEmissionConfig>;
    case JourneyNodeType.TimeDelay:
      return {
        ...getDefaultNodeDetails(),
        id,
        name: "Time delay",
        config: {
          type: JourneyNodeType.TimeDelay,
          delay: {
            unit: IntervalUnit.Day,
            quantity: 1,
          },
        },
      } satisfies JourneyNodeDetails<TimeDelayConfig>;
    case JourneyNodeType.WaitUntilEvent:
      return {
        ...getDefaultNodeDetails(),
        id,
        name: "Hold until",
        config: {
          type: JourneyNodeType.WaitUntilEvent,
          timeout_duration: {
            unit: IntervalUnit.Day,
            // Initial value is `undefined`, but type requires a number
            quantity: undefined as any,
          },
          event_conditions: {
            type: ConditionType.And,
            conditions: [],
          },
        },
      } satisfies JourneyNodeDetails<WaitUntilEventConfig>;
    case JourneyNodeType.Segments:
      return {
        ...getDefaultNodeDetails(),
        id,
        name: "Segment",
        config: { type: JourneyNodeType.Segments },
      } satisfies JourneyNodeDetails<SegmentsConfig>;
    case JourneyNodeType.Splits:
      return {
        ...getDefaultNodeDetails(),
        id,
        name: "A/B Split",
        config: { type: JourneyNodeType.Splits },
      } satisfies JourneyNodeDetails<SplitsConfig>;
    case JourneyNodeType.Decision:
      return {
        ...getDefaultNodeDetails(),
        id,
        name: "Decision",
        config: {
          type: JourneyNodeType.Decision,
          attribution: { window: { unit: IntervalUnit.Day, value: 7 } },
        },
      } satisfies JourneyNodeDetails<DecisionConfig>;
    case JourneyNodeType.Context:
      return {
        ...getDefaultNodeDetails(),
        id,
        name: "Set variable", // TODO(samwhale): Should this have a name?
        config: { type: JourneyNodeType.Context, values: [] },
      } satisfies JourneyNodeDetails<ContextConfig>;
    case JourneyNodeType.CouponAssignment:
      return {
        ...getDefaultNodeDetails(),
        id,
        name: "Assign coupon",
        config: {
          type: JourneyNodeType.CouponAssignment,
          pool_model_id: "",
          assignment_column: "",
          assignment_variable_name: "",
        },
      } satisfies JourneyNodeDetails<CouponAssignmentConfig>;
    case JourneyNodeType.Message: {
      const ct = messageType ?? ChannelType.Email;
      const channelLabel = {
        [ChannelType.Email]: "Email",
        [ChannelType.Sms]: "SMS",
        [ChannelType.Push]: "Push",
        [ChannelType.Whatsapp]: "WhatsApp",
        [ChannelType.InApp]: "In-app",
      } as const;

      return {
        ...getDefaultNodeDetails(),
        id,
        name: `${channelLabel[ct]} created ${format(new Date(), "MMMM d 'at' h:mm a")}`,
        config: {
          type: JourneyNodeType.Message,
          message_type: ct,
          ...(ct === ChannelType.InApp && {
            inapp_expiration: DEFAULT_IN_APP_MESSAGE_EXPIRATION,
          }),
        },
      } satisfies JourneyNodeDetails<MessageNodeConfig>;
    }
    case JourneyNodeType.Note:
      return {
        id,
        content: "",
        node_id: null,
        created_by: null,
        updated_by: null,
        created_at: null,
        updated_at: null,
      } satisfies Omit<JourneyNoteDetails, "position">;
    default:
      // Frontend only supports the above node types.
      throw new Error("Unsupported journey node type " + type);
  }
}

function getDefaultNodeDetails(): Omit<
  JourneyNodeDetails<JourneyNodeConfig>,
  "id" | "name" | "config"
> {
  return {
    description: null,
    segment_id: null,
    event_relationship_id: null,
    number_users: null,
    number_unique_users: null,
    sync_configs: null,
    message_config: null,
  };
}
